import { jsPDF } from "jspdf";
import dayjs from "dayjs";

/**
 * Production Batch label sheet builder (jsPDF).
 *
 * Every label is exactly 5 cm x 2.5 cm (50 mm x 25 mm). The PDF page itself is
 * that exact label size (NOT A4) and holds a single label, laid out in one
 * column - one label per page. This matches the physical label printer, so the
 * on-screen preview already shows the real label dimensions.
 *
 * Layout, top to bottom:
 *   Retail Price / Packed Date / Expire Date / Batch No
 *   then the company name as the final line, right aligned.
 * There is no horizontal rule in the design.
 *
 * Pagination is deterministic: exactly `quantity` pages are produced, so the
 * printed sheet always matches the requested count.
 */

// ---- Fixed label geometry (mm) ---------------------------------------------
export const LABEL_WIDTH = 50;
export const LABEL_HEIGHT = 25;

// One label per page.
export const LABELS_PER_PAGE = 1;

/**
 * Draws one 50 x 25 mm label on the current page.
 */
const drawLabel = (doc, label) => {
  const inset = 0.6;
  const left = 2.6;
  const right = LABEL_WIDTH - 2.6;

  // // Border
  // doc.setDrawColor(120, 120, 120);
  // doc.setLineWidth(0.2);
  // doc.rect(inset, inset, LABEL_WIDTH - inset * 2, LABEL_HEIGHT - inset * 2);

  // Retail price (the key figure - bold)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.text(`Retail Price: Rs. ${label.retailPrice}`, left, 6);

  // Dates + batch number
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(45, 45, 45);
  doc.text(`Packed Date: ${label.packedDate}`, left, 10.8);
  doc.text(`Expire Date: ${label.expireDate}`, left, 14.8);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(0, 0, 0);
  doc.text(`Batch No: ${label.batchNo}`, left, 18.8);

  // Company name - deliberately a different family/style (Times bold italic)
  // from the rest of the label, placed on the last line and right aligned.
  doc.setFont("times", "bolditalic");
  doc.setFontSize(7.6);
  doc.setTextColor(20, 20, 20);
  doc.text(label.company, right, 22.6, { align: "right" });
};

/**
 * Builds the label PDF sheet.
 *
 * @param {Object} options
 * @param {number} options.quantity    - Number of labels to print (> 0)
 * @param {string} options.retailPrice - Formatted retail price (e.g. "1,200.00")
 * @param {string} options.packedDate  - Packed date ("YYYY-MM-DD")
 * @param {string} options.expireDate  - Expire date ("YYYY-MM-DD")
 * @param {string} options.batchNo     - Batch reference printed on the label
 * @param {string} [options.company]   - Company name line
 * @returns {jsPDF} The generated (unsaved) document
 */
export const buildLabelPDF = ({
  quantity = 0,
  retailPrice = "",
  packedDate = "",
  expireDate = "",
  batchNo = "",
  company = "Weehena Mango (Pvt) Ltd",
}) => {
  // NOTE: jsPDF swaps a custom [width, height] format when orientation is
  // "portrait", so "landscape" is required to keep width 50 x height 25.
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: [LABEL_WIDTH, LABEL_HEIGHT] });
  const label = {
    company,
    retailPrice,
    packedDate: packedDate ? dayjs(packedDate).format("YYYY-MM-DD") : "-",
    expireDate: expireDate ? dayjs(expireDate).format("YYYY-MM-DD") : "-",
    batchNo: batchNo || "-",
  };

  const total = Math.max(0, parseInt(quantity, 10) || 0);

  for (let i = 0; i < total; i += 1) {
    if (i > 0) doc.addPage();
    drawLabel(doc, label);
  }

  return doc;
};

export default buildLabelPDF;
